import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { DeepPartial } from 'utility-types'
import { l } from '@manychat/manyui'
import { PlanSlug } from '@manychat/paywall'

import { MAX_KNOWLEDGE_ENTRIES } from 'apps/aiAutomation/lib/knowledge'
import { AiAutomationModel } from 'apps/aiAutomation/store/core/lib/model'
import { AiAutomationCoreState, LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { billing, UpgradeSource } from 'common/billing'
import { alert } from 'common/core'
import { isProOrTrialStatus } from 'common/core/models/Account/helpers'
import { getAccountStatus } from 'common/core/selectors/appSelectors'
import { AiAutomationApi } from 'shared/api/requests/aiAutomation'
import {
  AiAutomationConfig,
  KnowledgeEntry,
  FallbackAssignee,
} from 'shared/api/requests/aiAutomation/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

const NAMESPACE = 'aiAutomationCore'

export const AiAutomationCoreInitialState: AiAutomationCoreState = {
  config: {
    business_context: [],
    scenarios: {
      faq: {
        published: false,
        fallback_assignee: {
          group_id: null,
          user_id: null,
        },
      },
    },
  },
  selectedKnowledgeEntryIndex: null,
  loaderStatus: LoaderStatus.NOT_LOADED,
}

const { reducer, actions } = createSlice({
  initialState: AiAutomationCoreInitialState,
  name: NAMESPACE,
  reducers: {
    configUpdated: (state, action: PayloadAction<AiAutomationConfig>) => {
      state.config = action.payload
    },
    selectedEntryIndexUpdated: (state, action: PayloadAction<number | null>) => {
      state.selectedKnowledgeEntryIndex = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConfigFx.pending, (state) => {
      state.loaderStatus = LoaderStatus.DASHBOARD_LOADING
    })
    builder.addCase(getConfigFx.fulfilled, (state, action) => {
      state.config = action.payload
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateConfigFx.fulfilled, (state, action) => {
      state.config = action.payload
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateConfigFx.rejected, (state) => {
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateFaqScenarioStatusFx.pending, (state) => {
      state.loaderStatus = LoaderStatus.FAQ_LIVE_LOADING
    })

    builder.addMatcher(isAnyOf(editEntryFx.pending.match, addEntryFx.pending.match), (state) => {
      state.loaderStatus = LoaderStatus.KNOWLEDGE_EDITOR_LOADING
    })
    builder.addMatcher(
      isAnyOf(
        updateFaqScenarioStatusFx.fulfilled.match,
        editEntryFx.fulfilled.match,
        addEntryFx.fulfilled.match,
      ),
      (state) => {
        state.loaderStatus = LoaderStatus.LOADED
      },
    )
  },
})

const getConfigFx = createAsyncAction(`${NAMESPACE}/getConfigFx`, async () => {
  const { data } = await AiAutomationApi.getConfig()

  return data.config
})

const updateConfigFx = createAsyncAction(
  `${NAMESPACE}/updateConfigFx`,
  async (changes: DeepPartial<AiAutomationConfig>, { getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())

    const { data } = await AiAutomationApi.updateConfig({
      query: { client_id: getClientId() },
      body: { config: AiAutomationModel.applyChangesToConfig(config, changes) },
    })

    return data.config
  },
)

const removeEntryFx = createAsyncAction(
  `${NAMESPACE}/removeEntryFx`,
  async (index: number, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    await dispatch(
      updateConfigFx({
        business_context: AiAutomationModel.removeKnowledgeEntry(config.business_context, index),
      }),
    )

    if (isAutomationPublished) {
      alert(
        config.business_context.length === 1
          ? l.translate('All the info was deleted! AI will no longer answer questions.')
          : l.translate('Info deleted! AI won’t answer questions related to that anymore.'),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
)

const editEntryFx = createAsyncAction(
  `${NAMESPACE}/editEntryFx`,
  async (data: KnowledgeEntry, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const index = AiAlwaysOnAutomationsSelectors.getSelectedKnowledgeEntryIndex(
      getState(),
    ) as number

    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    await dispatch(
      updateConfigFx({
        business_context: AiAutomationModel.updateKnowledgeEntry(
          config.business_context,
          index,
          data,
        ),
      }),
    )

    if (isAutomationPublished) {
      alert(
        l.translate(
          'Knowledge updated. AI is now using the latest version to answer customer questions.',
        ),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
  {
    condition: (_, { getState }) =>
      Boolean(AiAlwaysOnAutomationsSelectors.getSelectedKnowledgeEntry(getState())),
  },
)

const addEntryFx = createAsyncAction(
  `${NAMESPACE}/addEntryFx`,
  async (data: KnowledgeEntry, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    if (config.business_context.length >= MAX_KNOWLEDGE_ENTRIES) {
      alert(
        l.translate('You can’t add more than {count} entries<l10n context="Knowledge entries"/>', {
          count: MAX_KNOWLEDGE_ENTRIES,
        }),
        'danger',
      )
      return
    }

    await dispatch(updateConfigFx({ business_context: [data, ...config.business_context] }))

    if (isAutomationPublished) {
      alert(l.translate('Info saved! AI will now use this to answer questions.'), 'success', {
        icon: 'CheckCircle',
      })
    }
  },
)

const updateFaqScenarioStatusFx = createAsyncAction(
  `${NAMESPACE}/updateFaqScenarioStatusFx`,
  async (status: boolean, { dispatch, getState }) => {
    const accountStatus = getAccountStatus(getState())

    if (status && !isProOrTrialStatus(accountStatus)) {
      billing.proAlert({
        products: [PlanSlug.AUTOMATION],
        source: UpgradeSource.AI_AUTOMATION_PUBLISH,
      })
      return
    }

    await dispatch(
      updateConfigFx({
        scenarios: {
          faq: {
            published: status,
          },
        },
      }),
    )

    alert(
      status
        ? l.translate('Your automation is live')
        : l.translate('Your automation has been stopped'),
      'success',
    )
  },
)

const updateFallbackAssigneeFx = createAsyncAction(
  `${NAMESPACE}/updateFallbackAssigneeFx`,
  async (fallback_assignee: FallbackAssignee, { dispatch }) => {
    await dispatch(
      updateConfigFx({
        scenarios: {
          faq: {
            fallback_assignee,
          },
        },
      }),
    )
  },
)

anotherTabNotificationsListener.on(
  'ai_automation_updated',
  (data: { config: AiAutomationConfig }, dispatch) => {
    dispatch(actions.configUpdated(data.config))
  },
)

export const AiAlwaysOnAutomationsActions = {
  selectedEntryIndexUpdated: actions.selectedEntryIndexUpdated,

  removeEntryFx,
  editEntryFx,
  addEntryFx,

  updateFaqScenarioStatusFx,
  updateFallbackAssigneeFx,
  getConfigFx,
}

export const AiAlwaysOnAutomationsReducer = reducer

const getState = (state: RootState) => state.aiAutomation.core

export const AiAlwaysOnAutomationsSelectors = {
  getState,
  getKnowledge: (state: RootState) => getState(state).config.business_context,
  getSelectedKnowledgeEntryIndex: (state: RootState) => getState(state).selectedKnowledgeEntryIndex,
  getSelectedKnowledgeEntry: (state: RootState) => {
    const index = getState(state).selectedKnowledgeEntryIndex
    if (index === null) {
      return null
    }
    return getState(state).config.business_context[index]
  },
  getKnowledgeUsage: createSelector(
    [(state) => getState(state).config.business_context],
    (knowledge) =>
      knowledge.reduce((size, knowledgeEntry) => size + knowledgeEntry.content.length, 0),
  ),
  getFaqScenarioStatus: (state: RootState) => getState(state).config.scenarios.faq.published,
  getConfig: (state: RootState) => getState(state).config,
  getLoaderStatus: (state: RootState) => getState(state).loaderStatus,
  getIsLoaded: (state: RootState) =>
    ![LoaderStatus.DASHBOARD_LOADING, LoaderStatus.NOT_LOADED].includes(
      getState(state).loaderStatus,
    ),
  getFallbackAssignee: createSelector(
    [(state) => getState(state).config.scenarios.faq.fallback_assignee],
    (assignee) => assignee?.user_id ?? assignee?.group_id ?? null,
  ),
}
